import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recurringSchedules } from '@/api/recurringSchedules';
import type { RecurringScheduleRow, RecurringStatus, RecurringPeriod } from '@/api/recurringSchedules';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/cn';
import { MoreHorizontal, Play, Pause, XCircle } from 'lucide-react';

const PERIOD_LABELS: Record<RecurringPeriod, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  monthly: 'Monthly',
  bimonthly: 'Every 2 months',
  quarterly: 'Quarterly',
  annually: 'Annually',
};

const STATUS_STYLES: Record<RecurringStatus, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  paused: { label: 'Paused', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  completed: { label: 'Completed', className: 'bg-sky-50 text-sky-700 border-sky-200' },
  failed: { label: 'Failed', className: 'bg-red-50 text-red-700 border-red-200' },
  cancelled: { label: 'Cancelled', className: 'bg-slate-50 text-slate-500 border-slate-200' },
};

function StatusPill({ status }: { status: RecurringStatus }) {
  const s = STATUS_STYLES[status];
  return (
    <span className={cn(
      'inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium leading-none',
      s.className,
    )}>
      {s.label}
    </span>
  );
}

function formatMoney(cents: number, currency = 'USD'): string {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100); }
  catch { return `${currency} ${(cents / 100).toFixed(2)}`; }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export function RecurringSchedulesPage() {
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<RecurringStatus | 'all'>('all');
  const canMutate = ['super_admin', 'reseller_owner', 'office_manager'].includes(user.role);

  const { data, isLoading, error } = useQuery({
    queryKey: ['recurringSchedules', statusFilter, effectiveCustomerId],
    queryFn: () => recurringSchedules.list({
      status: statusFilter === 'all' ? undefined : statusFilter,
      customer_id: effectiveCustomerId ?? undefined,
    }),
  });
  const rows = data?.rows ?? [];

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof recurringSchedules.update>[1] }) =>
      recurringSchedules.update(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recurringSchedules'] }),
  });
  const cancelMut = useMutation({
    mutationFn: (id: string) => recurringSchedules.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recurringSchedules'] }),
  });

  const columns = [
    { header: 'Amount', cell: (r: RecurringScheduleRow) => (
      <span className="font-mono text-sm tabular-nums font-semibold">{formatMoney(r.amount_cents, r.currency)}</span>
    ) },
    { header: 'Period', cell: (r: RecurringScheduleRow) => (
      <span className="text-sm text-slate-600">{PERIOD_LABELS[r.period]}</span>
    ) },
    { header: 'Progress', cell: (r: RecurringScheduleRow) => (
      <span className="text-xs text-slate-500">
        {r.charged_count} / {r.total_count ?? <span className="italic">indefinite</span>}
      </span>
    ) },
    { header: 'Next charge', cell: (r: RecurringScheduleRow) => (
      <div className="flex flex-col">
        <span className="text-xs text-slate-600">{formatDate(r.status === 'active' ? r.next_charge_at : null)}</span>
        {r.retry_at && r.status === 'active' && (
          <span className="text-[10px] text-amber-600">Retry at {formatDate(r.retry_at)}</span>
        )}
      </div>
    ) },
    { header: 'Status', cell: (r: RecurringScheduleRow) => <StatusPill status={r.status} /> },
    { header: '', className: 'w-10 text-end', cell: (r: RecurringScheduleRow) => (
      canMutate && (r.status === 'active' || r.status === 'paused') ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Row actions"
              onClick={(e) => e.stopPropagation()}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {r.status === 'active' && (
              <DropdownMenuItem onClick={() => updateMut.mutate({ id: r.id, patch: { status: 'paused' } })}>
                <Pause className="h-3.5 w-3.5 mr-2" /> Pause
              </DropdownMenuItem>
            )}
            {r.status === 'paused' && (
              <DropdownMenuItem onClick={() => updateMut.mutate({ id: r.id, patch: { status: 'active' } })}>
                <Play className="h-3.5 w-3.5 mr-2" /> Resume
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600 focus:bg-red-50"
              onClick={() => {
                if (confirm('Cancel this recurring charge? It will stop immediately; already-captured charges remain.')) {
                  cancelMut.mutate(r.id);
                }
              }}
            >
              <XCircle className="h-3.5 w-3.5 mr-2" /> Cancel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null
    ) },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Payments"
        title="Recurring schedules"
        description="Automatic recurring charges created through IVR payment nodes. The system charges the stored card on its period and retries declines on a configurable schedule."
        actions={
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
            <SelectTrigger className="h-9 w-40 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        }
      />
      {isLoading ? (
        <div className="text-sm text-slate-400">Loading schedules…</div>
      ) : error ? (
        <div role="alert" className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load schedules.'}
        </div>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          empty="No recurring schedules. Callers who opt into a recurring payment at an IVR payment node will show up here."
        />
      )}
    </>
  );
}
